import React, { useState } from 'react';
import BottomSheet from './BottomSheet';

function fmtDate(s) {
  const str = String(s ?? '');
  if (str.length === 8) return `${str.slice(0, 4)}.${str.slice(4, 6)}.${str.slice(6, 8)}`;
  return str;
}

function displayDate(s) {
  const value = fmtDate(s);
  return value || '일자 미상';
}

function normalizeCompactDate(value) {
  const str = String(value ?? '').trim();
  if (/^\d{8}$/.test(str)) return str;
  return '';
}

function metricChipStyle() {
  return {
    fontSize: 11,
    fontWeight: 700,
    padding: '4px 8px',
    borderRadius: 999,
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    color: '#475569',
    whiteSpace: 'nowrap',
  };
}

function labelBadgeStyle(kind) {
  if (kind === 'latest') {
    return {
      fontSize: 11,
      fontWeight: 700,
      color: '#2458cf',
      background: '#e8f0ff',
      padding: '3px 8px',
      borderRadius: 999,
      whiteSpace: 'nowrap',
      flexShrink: 0,
    };
  }
  if (kind === 'repeat') {
    return {
      fontSize: 11,
      fontWeight: 700,
      color: '#9a6507',
      background: '#fef3c7',
      padding: '3px 8px',
      borderRadius: 999,
      whiteSpace: 'nowrap',
      flexShrink: 0,
    };
  }
  if (kind === 'linked') {
    return {
      fontSize: 11,
      fontWeight: 700,
      color: '#0f766e',
      background: '#ccfbf1',
      padding: '3px 8px',
      borderRadius: 999,
      whiteSpace: 'nowrap',
      flexShrink: 0,
    };
  }
  return null;
}

function resolveRecordLabel(record) {
  if (record?.latest) return { text: '최신', kind: 'latest' };
  if (record?.tags?.includes('반복')) return { text: '반복', kind: 'repeat' };
  if (record?.tags?.includes('검진연계')) return { text: '검진 연계', kind: 'linked' };
  return null;
}

function buildTypeLabel(typeSet) {
  const list = Array.from(typeSet ?? []).filter(Boolean);
  if (list.length === 0) return '기타';
  if (list.length === 1) return list[0];
  return `${list[0]} 외 ${list.length - 1}`;
}

function groupRecordsByFacility(records, latestDate) {
  const grouped = new Map();

  (records ?? []).forEach((record, index) => {
    const facilityName = String(record?.facilityName ?? '').trim() || '미상 기관';
    const date = normalizeCompactDate(record?.date);
    const prescriptionCount = Number(record?.prescriptionCount ?? 0);
    const safePrescriptionCount = Number.isFinite(prescriptionCount) ? prescriptionCount : 0;

    if (!grouped.has(facilityName)) {
      grouped.set(facilityName, {
        facilityName,
        latestDate: date,
        latestRecord: record,
        visitCount: 1,
        prescriptionTotal: safePrescriptionCount,
        dates: new Set(date ? [date] : []),
        types: new Set(record?.type ? [record.type] : []),
        dateTypeMap: new Map(date ? [[date, new Set(record?.type ? [record.type] : [])]] : []),
        datePrescriptionMap: new Map(date ? [[date, safePrescriptionCount]] : []),
        tags: new Set(Array.isArray(record?.tags) ? record.tags : []),
        latest: Boolean(record?.latest),
        firstSeenOrder: index,
      });
      return;
    }

    const current = grouped.get(facilityName);
    current.visitCount += 1;
    current.prescriptionTotal += safePrescriptionCount;
    if (date) current.dates.add(date);
    if (record?.type) current.types.add(record.type);
    if (date) {
      const typeSet = current.dateTypeMap.get(date) ?? new Set();
      if (record?.type) typeSet.add(record.type);
      current.dateTypeMap.set(date, typeSet);
      current.datePrescriptionMap.set(date, (current.datePrescriptionMap.get(date) ?? 0) + safePrescriptionCount);
    }
    if (Array.isArray(record?.tags)) {
      record.tags.forEach((tag) => current.tags.add(tag));
    }
    if (record?.latest) current.latest = true;

    if (date && (!current.latestDate || date > current.latestDate)) {
      current.latestDate = date;
      current.latestRecord = record;
    }
  });

  return Array.from(grouped.values())
    .map((group) => {
      const sortedDates = Array.from(group.dates).sort((a, b) => b.localeCompare(a));
      const latestCompactDate = sortedDates[0] || group.latestDate || null;
      const latestTypeLabel = buildTypeLabel(group.dateTypeMap.get(latestCompactDate) ?? new Set(group.latestRecord?.type ? [group.latestRecord.type] : []));
      const previousVisits = sortedDates.slice(1).map((date) => ({
        date,
        typeLabel: buildTypeLabel(group.dateTypeMap.get(date)),
        prescriptionCount: group.datePrescriptionMap.get(date) ?? 0,
      }));

      return {
        facilityName: group.facilityName,
        latestDate: latestCompactDate,
        previousVisits,
        visitCount: group.visitCount,
        latestTypeLabel,
        prescriptionTotal: group.prescriptionTotal,
        latest: group.latest || (Boolean(latestDate) && latestCompactDate === latestDate),
        tags: Array.from(group.tags),
        firstSeenOrder: group.firstSeenOrder,
      };
    })
    .sort((a, b) => {
      const dateCompare = (b.latestDate || '').localeCompare(a.latestDate || '');
      if (dateCompare !== 0) return dateCompare;
      return a.firstSeenOrder - b.firstSeenOrder;
    });
}

const activeTabStyle = {
  padding: '8px 11px',
  borderRadius: 999,
  background: '#eff6ff',
  border: '1px solid #bfdbfe',
  color: '#1d4ed8',
  fontSize: 12,
  fontWeight: 710,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
};

const inactiveTabStyle = {
  padding: '8px 11px',
  borderRadius: 999,
  border: '1px solid #d6e3f3',
  background: '#fff',
  color: '#4b5563',
  fontSize: 12,
  fontWeight: 710,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
};

export default function NhisMedicalUsage({ medicalUsage }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(0);

  if (!medicalUsage) return null;

  const {
    totalVisits = 0,
    facilityCount = 0,
    latestDate,
    breakdown = {},
    recentRecords = [],
  } = medicalUsage;

  const breakdownEntries = Object.entries(breakdown ?? {});
  const topBreakdown = breakdownEntries.slice(0, 3);
  const remainingBreakdownCount = Math.max(0, breakdownEntries.length - topBreakdown.length);

  const tabLabels = ['전체', ...breakdownEntries.map(([type]) => type)];
  const filteredRecords = tab === 0
    ? (recentRecords ?? [])
    : (recentRecords ?? []).filter((rec) => rec.type === tabLabels[tab]);
  const groupedRecords = groupRecordsByFacility(filteredRecords, latestDate);

  const subHeader = (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      <span style={metricChipStyle()}>이용 기록 {totalVisits}건</span>
      <span style={metricChipStyle()}>방문 기관 {facilityCount}곳</span>
      {latestDate && <span style={metricChipStyle()}>최신 {fmtDate(latestDate)}</span>}
    </div>
  );

  return (
    <>
      <div style={{
        background: '#fff',
        border: '1px solid #dce7f2',
        borderRadius: 18,
        boxShadow: '0 10px 30px rgba(13,36,67,0.10)',
        padding: 14,
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}>
        <div style={{ marginBottom: 9 }}>
          <span style={{ fontSize: 15, fontWeight: 760, letterSpacing: '-0.01em', color: '#102036' }}>
            의료 이용 요약
          </span>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          <span style={metricChipStyle()}>이용 기록 {totalVisits}건</span>
          <span style={metricChipStyle()}>방문 기관 {facilityCount}곳</span>
          {latestDate && <span style={metricChipStyle()}>최신 {fmtDate(latestDate)}</span>}
        </div>

        {topBreakdown.length > 0 && (
          <p style={{ margin: '0 2px 10px', fontSize: 11, color: '#64748b', lineHeight: 1.45 }}>
            이용 유형: {topBreakdown.map(([type, count]) => `${type} ${count}`).join(' · ')}
            {remainingBreakdownCount > 0 ? ` · 외 ${remainingBreakdownCount}개 유형` : ''}
          </p>
        )}

        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 7 }}>
          {(recentRecords ?? []).slice(0, 3).map((rec, i) => {
            const label = resolveRecordLabel(rec);
            return (
              <li
                key={`${rec.facilityName || 'facility'}-${rec.date || 'date'}-${i}`}
                style={{
                  padding: '10px 11px',
                  borderRadius: 11,
                  border: '1px solid #e6edf7',
                  background: '#f9fcff',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 8,
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <b style={{ fontSize: 13, color: '#102036', lineHeight: 1.4, wordBreak: 'break-word' }}>
                    {rec.facilityName}
                  </b>
                  <small style={{ display: 'block', marginTop: 2, fontSize: 11, color: '#61758e' }}>
                    {fmtDate(rec.date)} · {rec.type}
                    {rec.prescriptionCount > 0 && ` · 처방 ${rec.prescriptionCount}건`}
                  </small>
                </div>
                {label && <span style={labelBadgeStyle(label.kind)}>{label.text}</span>}
              </li>
            );
          })}
        </ul>

        <button
          onClick={() => setOpen(true)}
          style={{
            marginTop: 12,
            width: '100%',
            height: 36,
            borderRadius: 8,
            border: '1px solid #1763ff',
            color: '#1763ff',
            background: 'transparent',
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          전체 이용 내역
        </button>
      </div>

      <BottomSheet open={open} onClose={() => setOpen(false)} title="전체 의료 이용 내역" subHeader={subHeader}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {tabLabels.map((lbl, i) => (
            <button key={lbl} onClick={() => setTab(i)} style={tab === i ? activeTabStyle : inactiveTabStyle}>
              {lbl}
            </button>
          ))}
        </div>

        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 8 }}>
          {groupedRecords.map((rec, i) => {
            const label = resolveRecordLabel({ latest: rec.latest, tags: rec.tags });
            const previewPrevVisits = rec.previousVisits.slice(0, 2);
            const remainingPrevCount = Math.max(0, rec.previousVisits.length - previewPrevVisits.length);
            return (
              <li
                key={`${rec.facilityName || 'facility'}-${rec.latestDate || 'date'}-${i}`}
                style={{
                  padding: '12px 14px',
                  border: '1px solid #e6edf7',
                  background: '#f9fcff',
                  borderRadius: 11,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 8,
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <b style={{ fontSize: 14, color: '#102036', lineHeight: 1.4, wordBreak: 'break-word' }}>
                    {rec.facilityName}
                  </b>
                  <small style={{ display: 'block', marginTop: 3, fontSize: 12, color: '#61758e' }}>
                    {displayDate(rec.latestDate)} · {rec.latestTypeLabel} · 방문 {rec.visitCount}회
                    {rec.prescriptionTotal > 0 && ` · 처방 ${rec.prescriptionTotal}건`}
                  </small>
                  {rec.previousVisits.length > 0 && (
                    <div
                      style={{
                        marginTop: 4,
                        display: 'grid',
                        gridTemplateColumns: '56px minmax(0, 1fr)',
                        columnGap: 6,
                        alignItems: 'start',
                      }}
                    >
                      <span style={{ fontSize: 12, color: '#6b7f99', fontWeight: 600, lineHeight: 1.45 }}>
                        이전 방문
                      </span>
                      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', rowGap: 3 }}>
                        {previewPrevVisits.map((visit, idx) => (
                          <li
                            key={`${visit.date}-${idx}`}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}
                          >
                            <span
                              style={{
                                width: 4,
                                height: 4,
                                borderRadius: 999,
                                background: '#94a3b8',
                                flexShrink: 0,
                              }}
                            />
                            <small style={{ fontSize: 12, color: '#6b7f99', lineHeight: 1.45, fontWeight: 500, minWidth: 0, wordBreak: 'break-word' }}>
                              {displayDate(visit.date)}({visit.typeLabel}{visit.prescriptionCount > 0 ? `·처방 ${visit.prescriptionCount}건` : ''})
                            </small>
                          </li>
                        ))}
                        {remainingPrevCount > 0 && (
                          <li style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                            <span
                              style={{
                                width: 4,
                                height: 4,
                                borderRadius: 999,
                                background: '#cbd5e1',
                                flexShrink: 0,
                              }}
                            />
                            <small style={{ fontSize: 12, color: '#6b7f99', lineHeight: 1.45, fontWeight: 500 }}>
                              외 {remainingPrevCount}회
                            </small>
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
                {label && <span style={labelBadgeStyle(label.kind)}>{label.text}</span>}
              </li>
            );
          })}
        </ul>
      </BottomSheet>
    </>
  );
}
